import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import { IconButton } from "./Button";
import {
  backdropVariants,
  drawerVariants,
  modalVariants,
} from "@/motion/variants";

/* -------------------------------------------------------------------------- */
/* shared behaviour                                                           */
/* -------------------------------------------------------------------------- */

/** Locks page scroll while any overlay is open, refcounted for nesting. */
let scrollLocks = 0;

function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    scrollLocks += 1;
    document.body.classList.add("no-scroll");
    return () => {
      scrollLocks -= 1;
      if (scrollLocks <= 0) {
        scrollLocks = 0;
        document.body.classList.remove("no-scroll");
      }
    };
  }, [active]);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Escape to close, Tab cycles inside, focus restored on unmount. */
function useDialogBehaviour(active: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  useScrollLock(active);

  useEffect(() => {
    if (!active) return;
    restoreTo.current = document.activeElement as HTMLElement | null;

    const node = ref.current;
    const focusFirst = window.setTimeout(() => {
      const target = node?.querySelector<HTMLElement>(FOCUSABLE);
      target?.focus();
    }, 80);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !node) return;

      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      if (focusable.length === 0) return;

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusFirst);
      document.removeEventListener("keydown", onKeyDown);
      restoreTo.current?.focus?.();
    };
  }, [active, onClose]);

  return ref;
}

/* -------------------------------------------------------------------------- */
/* Modal                                                                      */
/* -------------------------------------------------------------------------- */

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Hide the default header when the body supplies its own. */
  bare?: boolean;
}

export function Modal({ open, onClose, title, subtitle, children, bare }: ModalProps) {
  const ref = useDialogBehaviour(open, onClose);

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          className="modal-backdrop is-open"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <motion.div
            ref={ref}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {bare ? null : (
              <div className="modal__head">
                <div>
                  <h3>{title}</h3>
                  {subtitle ? <p>{subtitle}</p> : null}
                </div>
                <button
                  type="button"
                  className="modal__close"
                  onClick={onClose}
                  aria-label="Close dialog"
                >
                  <Icon name="close" />
                </button>
              </div>
            )}
            <div className="modal__body">{children}</div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* Drawer                                                                     */
/* -------------------------------------------------------------------------- */

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Drawer({ open, onClose, title, children, footer }: DrawerProps) {
  const ref = useDialogBehaviour(open, onClose);

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            className="drawer-backdrop is-open"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />
          <motion.aside
            ref={ref}
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="drawer__head">
              <h3>{title}</h3>
              <button type="button" className="modal__close" onClick={onClose} aria-label="Close filters">
                <Icon name="close" />
              </button>
            </div>
            <div className="drawer__body">{children}</div>
            {footer ? <div className="drawer__foot">{footer}</div> : null}
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* Lightbox                                                                   */
/* -------------------------------------------------------------------------- */

export interface LightboxImage {
  src: string;
  alt: string;
}

interface LightboxProps {
  images: LightboxImage[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export function Lightbox({ images, index, onClose, onIndexChange }: LightboxProps) {
  const open = index !== null;
  const ref = useDialogBehaviour(open, onClose);

  const step = useCallback(
    (delta: number) => {
      if (index === null || images.length === 0) return;
      onIndexChange((index + delta + images.length) % images.length);
    },
    [index, images.length, onIndexChange],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") step(-1);
      if (event.key === "ArrowRight") step(1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, step]);

  const current = index !== null ? images[index] : undefined;

  return createPortal(
    <AnimatePresence>
      {open && current ? (
        <motion.div
          ref={ref}
          className="lightbox is-open"
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <button type="button" className="lightbox__close" onClick={onClose} aria-label="Close image viewer">
            <Icon name="close" />
          </button>

          {images.length > 1 ? (
            <>
              <button
                type="button"
                className="lightbox__nav lightbox__nav--prev"
                onClick={() => step(-1)}
                aria-label="Previous image"
              >
                <Icon name="chevron-left" />
              </button>
              <button
                type="button"
                className="lightbox__nav lightbox__nav--next"
                onClick={() => step(1)}
                aria-label="Next image"
              >
                <Icon name="chevron-right" />
              </button>
            </>
          ) : null}

          <div>
            <AnimatePresence mode="wait">
              <motion.img
                key={current.src}
                src={current.src}
                alt={current.alt}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.99 }}
                transition={{ duration: 0.24 }}
              />
            </AnimatePresence>
            <div className="lightbox__caption">
              {current.alt} · {(index ?? 0) + 1} / {images.length}
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

/* -------------------------------------------------------------------------- */
/* Confirm-style footer helper                                                */
/* -------------------------------------------------------------------------- */

export function DrawerFooter({
  onClear,
  onApply,
  count,
}: {
  onClear: () => void;
  onApply: () => void;
  count: number;
}) {
  return (
    <>
      <button type="button" className="btn btn--ghost" onClick={onClear}>
        Clear all
      </button>
      <button type="button" className="btn btn--primary" onClick={onApply}>
        Show {count} {count === 1 ? "result" : "results"}
      </button>
    </>
  );
}

export { IconButton };
